import React, { useState, useEffect, useRef } from "react";
import {
  Alert,
  Box,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useMediaQuery,
  useTheme,
  Chip,
} from "@mui/material";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import moment from "moment";
import AddTourProgramInlineForm from "../Modal/AddTourProgramInlineForm";
import {
  MonthlyHolidayData,
  MonthlyTourProgramData,
} from "../../../../../domain/models/monthly-tour-program/fetch-monthly-tour-program";
import { AddCircleOutline, Delete } from "@mui/icons-material";
import { MonthlyTourProgramStore } from "../../../../../store/main/MonthlyTourProgramStore";
import ConfirmDeleteModal from "../Modal/ConfirmDeleteModal";

interface TourProgramCalendarViewProps {
  month: number;
  year: number;
  data: MonthlyTourProgramData[];
  holiday: MonthlyHolidayData[];
  editable: boolean;
  specialAccessDays: string[]; // ✅ Add this line
  refreshData: () => void;
}

const TourProgramCalendarView: React.FC<TourProgramCalendarViewProps> = ({
  month,
  year,
  data,
  holiday,
  editable,
  refreshData,
  specialAccessDays, // ✅ include it here
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { deleteTourProgram, fetchMonthlySmartFill } =
    MonthlyTourProgramStore();

  const [selectedDate, setSelectedDate] = useState<string | null>(null); // for form modal
  const [activeDate, setActiveDate] = useState<string | null>(null); // for table

  const [calendarHeight, setCalendarHeight] = useState<number>(0);
  const calendarRef = useRef<FullCalendar | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const handleClose = () => setSelectedDate(null);
  const handleSave = (formData: any) => {
    setSelectedDate(null);
    refreshData();
  };

  //   const handleDateClick = (arg: any) => {
  //     const clickedDate = arg.dateStr;

  //     // ✅ Always close any open modal when clicking a new date
  //     setSelectedDate(null);

  //     // ✅ Block clicks on holidays
  //    const isHoliday = holiday.some((h) => h.date === clickedDate);
  //   const isSunday = moment(clickedDate).day() === 0;
  //     const hasSpecialAccess = specialAccessDays.includes(clickedDate);

  //  if (isHoliday) {
  //     // ✅ Show Holiday in table, but don't open modal
  //     setActiveDate(clickedDate);
  //     setSelectedDate(null);
  //     return;
  //   }

  //     // ✅ Check if the clicked date has special access

  //     if (isSunday ||editable || hasSpecialAccess) {
  //       // ✅ Either editable month OR user has special access for this day
  //       setSelectedDate(clickedDate);
  //       setActiveDate(clickedDate);
  //     } else {
  //       // ❌ Only show toast when it's actually locked and not special access
  //       setSnackbarOpen(true);
  //       setActiveDate(clickedDate); // still show tours
  //       setSelectedDate(null); // ✅ close modal if it was open
  //     }
  //   };

  const handleDateClick = (arg: any) => {
    const clickedDate = arg.dateStr;

    // Always close any open modal first
    setSelectedDate(null);

    const isHoliday = holiday.some((h) => h.date === clickedDate);
    const isSunday = moment(clickedDate).day() === 0;
    const hasSpecialAccess = specialAccessDays.includes(clickedDate);
    const dayData = data.filter((item) => item.date === clickedDate);

    // Always show table
    setActiveDate(clickedDate);

    // 🧭 1️⃣ Holiday check
    if (isHoliday) {
      setToastMessage("This is a Holiday. You cannot add a tour on this day.");
      setSnackbarOpen(true);
      return;
    }

    // 🧭 2️⃣ Sunday rule — allow Field Work only
    if (isSunday && dayData.length === 0) {
      setSelectedDate(clickedDate); // allow Field Work
      return;
    }

    // 🧭 3️⃣ If the day has a non–Field Work type (Office Work, Transit, Meeting, Leave)
    const nonFieldEntry = dayData.find((d) => d.type !== "Field Work");
    if (nonFieldEntry) {
      setToastMessage(
        `This day is marked as ${nonFieldEntry.type}. You cannot add another tour.`
      );
      setSnackbarOpen(true);
      return;
    }

    // 🧭 4️⃣ Editable / special access rule
    if (editable || hasSpecialAccess) {
      setSelectedDate(clickedDate);
    } else {
      setToastMessage("Tour is locked for this month.");
      setSnackbarOpen(true);
    }
  };

  const initialDate = moment(`${year}-${month}-01`).format("YYYY-MM-DD");

  // Calculate calendar height based on weeks
  useEffect(() => {
    const startOfMonth = moment(`${year}-${month}-01`).startOf("week");
    const endOfMonth = moment(`${year}-${month}-01`)
      .endOf("month")
      .endOf("week");
    const weeks = endOfMonth.diff(startOfMonth, "weeks") + 1;
    const rowHeight = 40;
    setCalendarHeight(weeks * rowHeight);
  }, [month, year]);

  // useEffect(() => {
  //   if (!activeDate && data && data.length > 0) {
  //     const firstDateWithStatus = data[0].date;
  //     setActiveDate(firstDateWithStatus);
  //   }
  // }, [data, month, year, activeDate]);

  useEffect(() => {
    if (!activeDate && data && data.length > 0) {
      // only set when user has never selected a date yet
      const firstDateWithStatus = data[0].date;
      setActiveDate(firstDateWithStatus);
    }
  }, [data, month, year]);

  // Filter data for table rendering
  const getDayData = (date: string) => {
    const isHoliday = holiday.some((h) => h.date === date);
    const isSunday = moment(date).day() === 0;

    if (isHoliday) {
      return [
        {
          type: "Holiday",
          workingWith: "-",
          territory: "-",
          brick: "-",
          status: "-",
          raw: undefined,
        },
      ];
    }
    if (isSunday) {
      return [
        {
          type: "Sunday",
          workingWith: "-",
          territory: "-",
          brick: "-",
          status: "-",
          raw: undefined,
        },
      ];
    }
    const dayData = data.filter((item) => item.date === date);

    if (dayData.length === 0)
      return [
        {
          type: "-",
          workingWith: "-",
          territory: "-",
          brick: "-",
          status: "-",
          raw: undefined,
        },
      ];

    return dayData.map((item) => ({
      type: item.type || "-",
      workingWith: item.working_with?.full_name || "-",
      territory: item.hq_territory?.name || "-",
      brick: item.hq_brick?.name || "-",
      status: item.status === null ? "Tour Not Set" : item.status,
      raw: item, // this exists now
    }));
  };

  const renderStatusChip = (status: string | null) => {
    const commonStyle = {
      color: "#212121",
      fontWeight: 500,
      height: 20,
      fontSize: "0.7rem",
      borderRadius: "10px",
      px: 0.75,
    };
    switch (status) {
      case "active":
        return (
          <Chip
            label="Approved"
            size="small"
            sx={{ backgroundColor: "#6FC276", ...commonStyle }}
          />
        );
      case "inactive":
        return (
          <Chip
            label="Rejected"
            size="small"
            sx={{ backgroundColor: "#F08080", ...commonStyle }}
          />
        );
      case "pending":
        return (
          <Chip
            label="Approval Pending"
            size="small"
            sx={{ backgroundColor: "#FFE37A", ...commonStyle }}
          />
        );
      case null:
        return (
          <Chip
            label="Tour Not Set"
            size="small"
            sx={{ backgroundColor: "#FF964F", ...commonStyle }}
          />
        );
      default:
        return <Chip label={status} size="small" sx={commonStyle} />;
    }
  };

  const hasMeeting = (date: string) => {
    return data.some((item) => item.date === date && item.type === "Meeting");
  };

  const handleDeleteClick = (id: number) => {
    setSelectedDeleteId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDeleteId) return;
    if (isDeleting) return; // prevent double click
    setIsDeleting(true);
    try {
      await deleteTourProgram(selectedDeleteId);
      setDeleteModalOpen(false);
      setSelectedDeleteId(null);
      refreshData?.();
    } catch (err) {
      console.error("Error deleting tour program:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  const handleAddClick = (date: string, currentType?: string) => {
    const isLocked =
      !editable &&
      !specialAccessDays.includes(moment(date).format("YYYY-MM-DD"));

    // ✅ Always close modal first
    setSelectedDate(null);

    if (isLocked) {
      setSnackbarOpen(true); // ✅ show toast
      return; // ❌ stop execution
    }

    const dateEntries = data.filter((d) => d.date === date);
    const hasNonFieldWork = dateEntries.some((d) => d.type !== "Field Work");

    // ❌ Block if non–Field Work already exists and we’re not adding Field Work
    if (hasNonFieldWork && currentType !== "Field Work") {
      setSnackbarOpen(true);
      return;
    }

    // ✅ Allow modal for Field Work or empty day
    setSelectedDate(date);
    setActiveDate(date);
  };
  const isHolidayDay = activeDate
    ? holiday.some((h) => h.date === activeDate)
    : false;
  const isSundayDay = activeDate ? moment(activeDate).day() === 0 : false;

  return (
    <Box
      mt={1}
      display="flex"
      flexDirection={isMobile ? "column" : "row"}
      gap={3}
      alignItems="flex-start"
      width="100%"
    >
      {/* Calendar */}
      <Paper
        elevation={1}
        sx={{
          p: 1,
          borderRadius: 2,
          flex: isMobile ? "1" : "0 0 500px",
          width: isMobile ? "100%" : "500px",
          border: "2px solid #f5f1f1ff", // ✅ Black border around the calendar
          "& .fc": {
            border: "1px solid #f5f1f1ff", // ✅ Border for the full calendar wrapper
          },
          "& .fc-scrollgrid": {
            border: "1px solid #f5f1f1ff", // ✅ Adds black border around the calendar grid
          },

          "& .fc-daygrid-day-number": {
            fontSize: "0.75rem",
            fontWeight: 600,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            position: "relative",
            textAlign: "center",
          },
          "& .fc-toolbar": { display: "none" },
          "& .fc-daygrid-day": {
            minWidth: "40px",
            height: "50px !important", // ⬆️ increased height
            border: "1px solid #fafafa",
            padding: 0,
            margin: 0,
            verticalAlign: "middle",
          },
          "& .fc-daygrid-day-frame": {
            minHeight: "50px !important", // ⬆️ match inner frame height
            height: "50px !important",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          },
          "& .fc-daygrid-week": {
            height: "50px !important", // ⬆️ ensure week row consistent
            maxHeight: "50px !important",
          },
          "& .tour-text": {
            fontSize: "0.6rem",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },

          overflow: "hidden",
        }}
      >
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          initialDate={initialDate}
          events={[]}
          dateClick={handleDateClick}
          height={calendarHeight}
          contentHeight="auto"
          showNonCurrentDates={false}
          fixedWeekCount={false}
          validRange={{
            start: moment(`${year}-${month}-01`)
              .startOf("month")
              .format("YYYY-MM-DD"),
            end: moment(`${year}-${month}-01`)
              .endOf("month")
              .add(1, "day")
              .format("YYYY-MM-DD"),
          }}
          dayCellDidMount={(info) => {
            const cellDate = moment(info.date);
            const formattedDate = cellDate.format("YYYY-MM-DD");
            const currentMonth = moment(`${year}-${month}-01`).month();

            // ❌ Skip cells that are not part of the current month — keep them white
            if (cellDate.month() !== currentMonth) {
              info.el.style.backgroundColor = "#f3f3f3ff";
              return;
            }

            // ✅ Check if this date is a holiday or Sunday
            const isHoliday = holiday.some((h) => h.date === formattedDate);
            const isSunday = cellDate.day() === 0; // 0 = Sunday
            const dayEvents = data.filter(
              (item) => item.date === formattedDate
            );

            let bgColor = "#FEAA71";
            let displayText = "Tour Not Set";

            if (isHoliday || isSunday) {
              // ✅ Grey color for holidays
              bgColor = "#E0E0E0";
              displayText = isSunday ? "Sunday" : "Holiday";
              // ✅ Make the cell non-clickable
              info.el.style.pointerEvents = "none";
              info.el.style.cursor = "not-allowed";
            } else if (dayEvents.length > 0) {
              // ✅ If any tour has pending status, mark the cell as "Approval Pending"
              const hasPending = dayEvents.some(
                (ev) => ev.status === "pending"
              );
              const hasActive = dayEvents.some((ev) => ev.status === "active");
              const hasRejected = dayEvents.some(
                (ev) => ev.status === "in_active"
              );

              if (hasPending) {
                bgColor = "#FEE894";
                displayText = "Approval Pending";
              } else if (hasActive) {
                bgColor = "#99D39E";
                displayText = "Approved";
              } else if (hasRejected) {
                bgColor = "#F29898";
                displayText = "Rejected";
              } else {
                bgColor = "#FEAA71";
                displayText = "Tour Not Set";
              }
            }

            // ✅ Apply background
            info.el.style.backgroundColor = bgColor;
            info.el.style.position = "relative";
            info.el.style.height = "40px";
            info.el.style.padding = "2px";
            info.el.style.overflow = "hidden";

            // ✅ Style the date number
            const dayNumberEl = info.el.querySelector(
              ".fc-daygrid-day-number"
            ) as HTMLElement;
            if (dayNumberEl) {
              dayNumberEl.style.position = "absolute";
              dayNumberEl.style.top = "2px";
              dayNumberEl.style.left = "2px";
              dayNumberEl.style.fontSize = "0.55rem";
              dayNumberEl.style.fontWeight = "600";
            }

            // ✅ Remove any old label
            const existingText = info.el.querySelector(".tour-text");
            if (existingText) info.el.removeChild(existingText);

            // ✅ Add label in center
            const textDiv = document.createElement("div");
            textDiv.className = "tour-text";
            textDiv.innerText = displayText;
            textDiv.style.position = "absolute";
            textDiv.style.top = "50%";
            textDiv.style.left = "50%";
            textDiv.style.transform = "translate(-50%, -50%)";
            textDiv.style.fontSize = "0.50rem";
            textDiv.style.fontWeight = "500";
            textDiv.style.whiteSpace = "nowrap";
            textDiv.style.textAlign = "center";
            info.el.appendChild(textDiv);
          }}
        />
      </Paper>

      {/* Table + Form */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        <TableContainer
          component={Paper}
          sx={{ borderRadius: 2, boxShadow: 3 }}
        >
          <Table
            sx={{
              "& td, & th": {
                fontSize: "0.75rem", // makes text smaller
                padding: "4px 8px", // reduces spacing
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell align="center">Day</TableCell>
                <TableCell align="center">Type</TableCell>
                <TableCell align="center">Working With</TableCell>
                <TableCell align="center">Territory</TableCell>
                <TableCell align="center">Brick</TableCell>
                <TableCell align="center">Status</TableCell>
                {(editable || specialAccessDays.length > 0) && (
                  <TableCell align="center">Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {activeDate ? (
                getDayData(activeDate).map((row, idx) => (
                  <TableRow key={`${activeDate}-${idx}`}>
                    <TableCell align="center">
                      {moment(activeDate).format("D MMM")}
                    </TableCell>
                    <TableCell align="center">{row.type}</TableCell>
                    <TableCell align="center">{row.workingWith}</TableCell>
                    <TableCell align="center">{row.territory}</TableCell>
                    <TableCell align="center">{row.brick}</TableCell>
                    <TableCell align="center">
                      {renderStatusChip(row.status || null)}
                    </TableCell>

                    {(editable || specialAccessDays.length > 0) && (
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={1}>
                          {/* ✅ Add Button */}
                          <IconButton
                            size="small"
                            sx={{
                              color: isHolidayDay ? "#b0b0b0" : "#1976d2",
                              "&:hover": {
                                color: isHolidayDay ? "#b0b0b0" : "#115293",
                              },
                            }}
                            onClick={() =>
                              handleAddClick(activeDate, row.raw?.type)
                            }
                            disabled={
                              isHolidayDay || // ✅ disable if holiday
                              (!editable &&
                                !specialAccessDays.includes(
                                  moment(activeDate).format("YYYY-MM-DD")
                                )) ||
                              (data.some(
                                (item) =>
                                  item.date === activeDate &&
                                  item.type !== "Field Work" &&
                                  row.raw?.type !== "Field Work"
                              ) &&
                                row.raw?.type !== "Field Work")
                            }
                          >
                            <AddCircleOutline fontSize="small" />
                          </IconButton>

                          {/* ✅ Delete Button */}
                          {row.raw?.id && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(row.raw.id)}
                              disabled={
                                isHolidayDay || // ✅ disable delete if holiday
                                (!editable &&
                                  !specialAccessDays.includes(
                                    moment(activeDate).format("YYYY-MM-DD")
                                  ))
                              }
                              sx={{
                                color: isHolidayDay ? "#e0e0e0" : "#d32f2f",
                                "&:hover": {
                                  color: isHolidayDay ? "#e0e0e0" : "#9a0007",
                                },
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={editable ? 7 : 6}>
                    Select a date from the calendar to view tours
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {selectedDate &&
          (editable ||
            specialAccessDays.includes(
              moment(selectedDate).format("YYYY-MM-DD")
            )) && (
            <AddTourProgramInlineForm
              selectedDate={selectedDate}
              onClose={handleClose}
              onSave={handleSave}
              tourTypes={[
                "Field Work",
                "Meeting",
                "Transit",
                "Office Work",
                "leave",
                "holiday",
              ]}
              hasMeeting={hasMeeting(selectedDate)} // pass meeting info
              existingTours={data.filter((item) => item.date === selectedDate)} // ✅ add this line
            />
          )}
        <ConfirmDeleteModal
          open={deleteModalOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          isDeleting={isDeleting} // ✅ pass down
        />

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity="warning"
            sx={{
              width: "100%",
              backgroundColor: "#b64a4aff",
              color: "#ffffffff",
              "& .MuiAlert-icon": {
                color: "#ffffff", // 👈 Change icon color here
              },
            }}
          >
            {toastMessage || "Tour is locked"}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default TourProgramCalendarView;
